import { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import TableEmptyRow from 'components/others/TableEmptyRow';
import useSettingsParams from 'hooks/useSettingsParams';
import * as apiObject4Team from 'api/apiObject4Team';
import { formatQty } from '../../../ObjectCard/ProposalShortage/model/formatQty';
import { isObjectEditLocked } from 'utils/helpers/objects/isObjectEditLocked';
import TeamProposalShortage, { type TeamShortageRow } from '../TeamProposalShortage';
import { teamShortageKey } from '../TeamProposalShortage/TeamShortageTable';

interface ObjectServiceRow {
    id: number;
    serviceId: number;
    name: string;
    count: number;
}

interface ObjectCardServicesProps {
    objectId: number;
    vobjectFull: {
        object?: { objectStatus?: number };
        services?: ObjectServiceRow[];
        proposalShortage?: {
            name?: string;
            services?: TeamShortageRow[];
        } | null;
    } | null;
    fetchObject: () => Promise<void> | void;
}

const ObjectCardServices = ({ objectId, vobjectFull, fetchObject }: ObjectCardServicesProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const params = useSettingsParams();
    const [addingKey, setAddingKey] = useState('');
    const locked = isObjectEditLocked(vobjectFull?.object?.objectStatus, params);
    const shortage = vobjectFull?.proposalShortage;
    const items = shortage?.services || [];
    const services = vobjectFull?.services || [];

    const handleAdd = async (item: TeamShortageRow) => {
        const key = teamShortageKey('services', item);
        if (addingKey || locked) return;
        setAddingKey(key);
        const ok = await apiObject4Team.addService(objectId, {
            serviceId: Number(item.serviceId || 0),
            name: item.name,
            count: Number(item.deficit || 0),
        });
        if (ok) {
            showToast(t('messExpandAdd'), 'success');
            await fetchObject();
        } else {
            showToast(t('common:messError'), 'danger');
        }
        setAddingKey('');
    };

    return (
        <div className="px-3">
            <TeamProposalShortage
                kind="services"
                proposalName={String(shortage?.name || '')}
                items={items}
                canAdd={!locked}
                addingKey={addingKey}
                onAdd={(item) => {
                    void handleAdd(item);
                }}
            />
            <div className="crm-table-wrap">
                <Table className="table-hover">
                    <thead>
                        <tr className="bg-light">
                            <td className="head-service">{t('fieldsService.service')}</td>
                            <td className="head-count text-end">{t('productFields.count')}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {!services.length ? (
                            <TableEmptyRow colSpan={2} />
                        ) : (
                            services.map((row) => (
                                <tr key={row.id}>
                                    <td className="text-truncate" title={row.name}>
                                        {row.name}
                                    </td>
                                    <td className="text-end text-nowrap">{formatQty(row.count)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default ObjectCardServices;
